import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../Firebase';
import NavigationBar from '../../NavigationBar';
import ClientCard from './ClientCard';

const Modal = ({ children, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-50 flex justify-center items-center">
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg max-w-sm w-full relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-lg font-bold text-black dark:text-white">&times;</button>
        {children}
      </div>
    </div>
  );
};

const AddClientForm = ({ onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      if (user) {
        await addDoc(collection(db, 'users', user.uid, 'clients'), formData);
        onClose();
      } else {
        console.error("User not authenticated");
      }
    } catch (error) {
      console.error("Error adding client: ", error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 rounded-lg">
      <input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleChange} required className="border-2 border-gray-300 dark:border-gray-600 p-3 w-full rounded-lg focus:outline-none focus:border-green-500 dark:focus:border-green-500 transition-colors" />
      <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} className="border-2 border-gray-300 dark:border-gray-600 p-3 w-full rounded-lg focus:outline-none focus:border-green-500 dark:focus:border-green-500 transition-colors" />
      <input type="text" name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} className="border-2 border-gray-300 dark:border-gray-600 p-3 w-full rounded-lg focus:outline-none focus:border-green-500 dark:focus:border-green-500 transition-colors" />
      <div className="flex justify-end space-x-4">
        <button type="button" onClick={onClose} className="bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-black dark:text-white font-semibold p-3 rounded-lg transition-colors">Cancel</button>
        <button type="submit" className="bg-black dark:bg-white hover:bg-green-500 dark:hover:bg-green-500 text-white dark:text-black font-semibold p-3 rounded-lg transition-colors">Add Client</button>
      </div>
    </form>
  );
};

const ClientDashboard = () => {
  const [clients, setClients] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      const user = auth.currentUser;
      if (user) {
        const clientsRef = collection(db, 'users', user.uid, 'clients');
        const unsubscribe = onSnapshot(clientsRef, snapshot => {
          const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setClients(clientsData);
        });
        return () => unsubscribe();
      }
    };

    fetchClients();
  }, []);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleDeleteClient = async (id) => {
    try {
      const user = auth.currentUser;
      if (user) {
        await deleteDoc(doc(db, 'users', user.uid, 'clients', id));
      } else {
        console.error("User not authenticated");
      }
    } catch (error) {
      console.error("Error deleting client: ", error);
    }
  };

  return (
    <div className="bg-white dark:bg-black">
      <NavigationBar />
      <div className="text-black dark:text-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Clients</h1>
            <button
              onClick={handleOpenModal}
              className="bg-black dark:bg-white text-white dark:text-black px-4 py-2 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
            >
              Add Client
            </button>
          </div>

          <input 
            type="text"
            placeholder="Search clients"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full mb-6 p-3 border rounded-lg"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients
              .filter(client => 
                client.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(client => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onDelete={handleDeleteClient}
                />
              ))}
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <AddClientForm onClose={handleCloseModal} />
      </Modal>
    </div>
  );
};

export default ClientDashboard;
